import * as Flowbite from 'flowbite-react';
import { useMemo, useCallback, useState, Fragment, ReactNode } from 'react';
import { BaseInputProps } from 'shared/Input/types';
import { IconX, IconDots } from '@tabler/icons-react';
import { Badge } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Button from 'shared/Button';
import { theme as dropdownTheme } from 'shared/Dropdown/Dropdown';
import { getReplaceThemeProps } from 'utils/replaceTheme';

export type TagValue = string | number;
type AllowedTagTuple = [string, TagValue, string?];
export type AllowedTag = TagValue | AllowedTagTuple;

export const isTagIncluded = (tag: TagValue, allowedTags?: AllowedTag[]) =>
    allowedTags?.find((s) => (Array.isArray(s) ? s[1] : s) === tag) !==
    undefined;

export interface RenderTagParams<T = TagValue> {
    tag: T;
    label: TagValue;
    onRemove: () => void;
    locked?: boolean;
}

export type TagProps<T = TagValue> = BaseInputProps<T[]> & {
    validateFn?: (value: T) => string | undefined;
    onValidate?: (message?: string) => void;
    mapToValue?: (value: TagValue) => T | undefined;
    mapToLabel?: (value: T) => TagValue;
    allowedTags?: AllowedTag[];
    selectOnly?: boolean;
    renderTag?: (params: RenderTagParams<T>) => ReactNode | undefined;
};

export const Tag = <T = TagValue,>({
    onChange = () => {},
    label,
    color = 'primary',
    value = [],
    validateFn = () => undefined,
    onValidate = () => {},
    mapToValue = (v) => v as T,
    mapToLabel,
    allowedTags: _allowedTags,
    selectOnly = false,
    renderTag,
    ...props
}: TagProps<T>) => {
    const { t } = useTranslation('common');
    const locked = props.disabled || props.readOnly;

    const [inputValue, setInputValue] = useState('');

    const allowedTags = useMemo(() => {
        if (!_allowedTags) return undefined;
        return _allowedTags.map<AllowedTagTuple>((tag) =>
            Array.isArray(tag) ? tag : [`${tag}`, tag]
        ) as AllowedTagTuple[];
    }, [_allowedTags]);

    const getTagLabel = useCallback(
        (tag: T): TagValue => {
            if (mapToLabel) return mapToLabel(tag);
            const tagLabel = allowedTags?.find(([, t]) => tag === t);
            return tagLabel ? tagLabel[0] : (tag as TagValue);
        },
        [allowedTags, mapToLabel]
    );

    const tags = useMemo(() => {
        const seen = new Set<TagValue>();
        return value.filter((tag) => {
            const label = getTagLabel(tag);
            if (seen.has(label)) return false;
            seen.add(label);
            return true;
        });
    }, [value, getTagLabel]);

    const getTagValue = useCallback(
        (v: TagValue) => mapToValue(typeof v === 'string' ? v.trim() : v),
        [mapToValue]
    );

    const onRemove = useCallback(
        (v: T) => {
            const newTags = value.filter((tag) => tag !== v);
            onChange(newTags);
        },
        [onChange, value]
    );

    const onAdd = useCallback(
        (addValue: T) => {
            const label = getTagLabel(addValue);
            const isDuplicate = value.some(
                (existing) => getTagLabel(existing) === label
            );
            if (isDuplicate) {
                setInputValue('');
                return;
            }
            onChange([...value, addValue]);
            setInputValue('');
            onValidate();
        },
        [onChange, value, onValidate, getTagLabel]
    );

    const onBlur = useCallback(() => {
        const v = getTagValue(inputValue);
        if (!v) return;

        const errorMessage = validateFn(v);
        if (!errorMessage) {
            onAdd(v);
        } else {
            setInputValue('');
            onValidate();
        }
    }, [getTagValue, inputValue, onAdd, onValidate, validateFn]);

    const onEnter = useCallback(
        (value: TagValue) => {
            const v = getTagValue(value);
            if (!v) return;

            const errorMessage = validateFn(v);
            errorMessage ? onValidate(errorMessage) : onAdd(v);
        },
        [onAdd, onValidate, validateFn, getTagValue]
    );

    const availableTags = useMemo(
        () =>
            allowedTags?.filter(
                ([label]) => !tags.some((tag) => getTagLabel(tag) === label)
            ),
        [allowedTags, tags, getTagLabel]
    );
    const includeDropdown = Array.isArray(availableTags) && !locked;

    return (
        <Flowbite.Label
            className="flex flex-col gap-2 font-normal"
            color={color}
        >
            {label && <span className="font-medium">{label}</span>}
            <div
                className={classNames(
                    'flex flex-col-reverse gap-2 p-2 border rounded-lg bg-gray-50',
                    color === 'failure'
                        ? 'border-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
                        : 'border-gray-300 focus-within:border-primary-600 focus-within:ring-1 focus-within:ring-primary-600',
                    { 'opacity-50 cursor-not-allowed': locked }
                )}
            >
                <div className="flex flex-wrap items-center gap-1">
                    <input
                        {...props}
                        disabled={props.disabled || selectOnly}
                        className={classNames(
                            'flex-1 bg-transparent border-0 outline-none focus:ring-0 shadow-none p-0 text-base text-gray-900 placeholder-gray-400 disabled:cursor-not-allowed',
                            props.className
                        )}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onBlur={onBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                onEnter(inputValue);
                            }
                        }}
                    />

                    {includeDropdown && (
                        <Flowbite.Dropdown
                            {...getReplaceThemeProps(dropdownTheme)}
                            label=""
                            renderTrigger={() => (
                                <Button
                                    aria-label={t('open')}
                                    disabled={availableTags.length == 0}
                                    color="light"
                                    outline
                                    size="iconXxs"
                                    className="ml-auto"
                                >
                                    <IconDots />
                                </Button>
                            )}
                            size="xs"
                            arrowIcon={false}
                            placement="bottom-end"
                            className="max-h-96 overflow-auto"
                        >
                            {availableTags.map(
                                ([label, tagValue, subLabel]) => (
                                    <Flowbite.DropdownItem
                                        key={tagValue}
                                        onClick={() => {
                                            const v = mapToValue(tagValue);
                                            if (v !== undefined) onAdd(v);
                                        }}
                                        className="text-wrap"
                                    >
                                        {label === `${tagValue}` ? (
                                            label
                                        ) : (
                                            <span className="flex flex-col">
                                                <span>{label}</span>
                                                {subLabel && (
                                                    <span className="text-xs text-gray-500">
                                                        {subLabel}
                                                    </span>
                                                )}
                                                <span className="text-xs text-gray-500 font-mono">
                                                    {tagValue}
                                                </span>
                                            </span>
                                        )}
                                    </Flowbite.DropdownItem>
                                )
                            )}
                        </Flowbite.Dropdown>
                    )}
                </div>

                <div className="flex gap-2 flex-wrap empty:hidden">
                    {tags.map((tag) => {
                        const tagLabel = getTagLabel(tag);

                        const customTag = renderTag?.({
                            tag,
                            label: tagLabel,
                            onRemove: () => onRemove(tag),
                            locked,
                        });

                        if (customTag !== undefined) {
                            return (
                                <Fragment key={tagLabel}>{customTag}</Fragment>
                            );
                        }

                        const hasMappedLabel = !mapToLabel && tagLabel !== tag;
                        const tagText = hasMappedLabel ? (
                            <Flowbite.Popover
                                trigger="hover"
                                content={
                                    <span className="p-2 font-mono block">
                                        {`${tag}`}
                                    </span>
                                }
                            >
                                <span>{tagLabel}</span>
                            </Flowbite.Popover>
                        ) : (
                            <span>{tagLabel}</span>
                        );

                        return (
                            <Badge
                                color="gray"
                                className="bg-gray-200 hover:bg-gray-300"
                                size="sm"
                                key={tagLabel}
                            >
                                <span className="flex font-medium items-center gap-1">
                                    {tagText}
                                    {!locked && (
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                onRemove(tag);
                                            }}
                                            aria-label={t('remove')}
                                            className="inline-flex items-center justify-center p-0 border-0 bg-transparent"
                                        >
                                            <IconX
                                                size={12}
                                                aria-hidden="true"
                                                focusable="false"
                                            />
                                        </button>
                                    )}
                                </span>
                            </Badge>
                        );
                    })}
                </div>
            </div>
        </Flowbite.Label>
    );
};
